import { Fragment, useEffect, useCallback, useState, useMemo, useRef } from 'react';
import { Page } from '../theme/elements/Page';
import { Box, Stack, Typography, useTheme, Button, CircularProgress } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import { useAppSelector, useAppDispatch } from 'hooks/redux.hooks';
import { DialogBox } from 'components/DialogBox';
import { selectTranslations } from 'state/reducers/translation.reducers';
import { setSuccessMessage } from 'state/reducers/ui.reducers';
import {
	getPaymentCategories,
	getPaymentCategoriesLoading,
	setError,
	getPaymentCategoriesError,
} from 'state/reducers/payment-categories.reducer';
import { PaymentCategoryTree } from 'sections/PaymentCategoriesManagement';
import { usePendingChanges } from 'sections/PaymentCategoriesManagement/hooks/usePendingChanges';
import * as PaymentCategoriesService from 'services/payment-categories.service';
import { WarningDialog } from 'components/WarningDialog';
import { initialMessage } from 'constants/ui.constants';

export const PaymentCategoriesApp = () => {
	const t = useAppSelector(selectTranslations);
	const theme = useTheme();
	const dispatch = useAppDispatch();

	// Redux state
	const originalCategories = useAppSelector(getPaymentCategories);
	const isLoading = useAppSelector(getPaymentCategoriesLoading);
	const errorState = useAppSelector(getPaymentCategoriesError);
	// Local state for batch operations (suppresses sync in usePendingChanges hook)
	const [isBatching, setIsBatching] = useState(false);
	// Local state for sync to companies operation
	const [isSyncing, setIsSyncing] = useState(false);
	// Track if new categories were added since last sync (session-based)
	const [hasNewCategoriesSinceLastSync, setHasNewCategoriesSinceLastSync] = useState(false);
	// Ref to store category IDs at time of last sync (or initial load)
	const syncedCategoryIdsRef = useRef<Set<number>>(new Set());
	// Sync confirmation dialog state
	const [syncDialogOpen, setSyncDialogOpen] = useState(false);

	// Error dialog handling - following SystemSettings.tsx pattern
	const errorDialogOpen = useMemo(() => {
		return errorState?.exist || false;
	}, [errorState?.exist]);

	const errorDialogMessage = useMemo(() => {
		if (errorState?.exist && errorState?.text) {
			return errorState.text;
		}
		return '';
	}, [errorState?.exist, errorState?.text]);

	const handleErrorDialogClose = useCallback(() => {
		dispatch(setError(initialMessage));
	}, [dispatch]);

	// Use pending changes hook for batch save functionality
	const {
		workingCategories,
		pendingChanges,
		hasPendingChanges,
		pendingChangesCount,
		addToggle,
		clearAll,
		getPendingStatus,
	} = usePendingChanges(originalCategories, isBatching);

	// Helper to collect all category IDs recursively
	const getAllCategoryIds = useCallback((categories: typeof originalCategories): Set<number> => {
		const ids = new Set<number>();
		const traverse = (cats: typeof originalCategories) => {
			cats.forEach((cat) => {
				ids.add(cat.id);
				if (cat.children?.length) traverse(cat.children);
			});
		};
		traverse(categories);
		return ids;
	}, []);

	// Fetch categories on mount
	useEffect(() => {
		dispatch(PaymentCategoriesService.getPaymentCategories() as any);
		dispatch(PaymentCategoriesService.fetchPaymentCategoryTranslations() as any);
	}, [dispatch]);

	// Detect new categories added since last sync
	useEffect(() => {
		if (originalCategories.length === 0) return;

		const currentIds = getAllCategoryIds(originalCategories);

		// Initialize synced IDs on first load
		if (syncedCategoryIdsRef.current.size === 0) {
			syncedCategoryIdsRef.current = currentIds;
			return;
		}

		// Check if there are new categories not in the synced set
		const hasNew = Array.from(currentIds).some((id) => !syncedCategoryIdsRef.current.has(id));
		if (hasNew) {
			setHasNewCategoriesSinceLastSync(true);
		}
	}, [originalCategories, getAllCategoryIds]);

	// Local change handlers (add to pending changes, don't call API yet)
	const handleToggleActive = useCallback(
		(categoryId: number, isActive: boolean) => {
			addToggle(categoryId, isActive);
		},
		[addToggle]
	);

	// Cancel all pending changes
	const handleCancelChanges = useCallback(() => {
		clearAll();
	}, [clearAll]);

	// Save all pending changes to the server
	const handleSave = useCallback(async () => {
		if (!hasPendingChanges) return;

		setIsBatching(true);

		try {
			// Process changes sequentially
			for (const change of pendingChanges) {
				switch (change.type) {
					case 'create':
						if (change.name && change.parentId !== undefined) {
							await dispatch(
								PaymentCategoriesService.createPaymentCategory({
									name: change.name,
									parentId: change.parentId,
								}) as any
							);
						}
						break;
					case 'update':
						if (change.name && change.categoryId > 0) {
							await dispatch(
								PaymentCategoriesService.updatePaymentCategory(change.categoryId, {
									name: change.name,
								}) as any
							);
						}
						break;
					case 'toggle':
						if (change.isActive !== undefined && change.categoryId > 0) {
							await dispatch(
								PaymentCategoriesService.togglePaymentCategoryActive(
									change.categoryId,
									change.isActive
								) as any
							);
						}
						break;
				}
			}

			// Refresh from server (useEffect will reset working state on originalCategories change)
			await dispatch(PaymentCategoriesService.getPaymentCategories() as any);
			await dispatch(PaymentCategoriesService.fetchPaymentCategoryTranslations() as any);

			// Show success toast
			dispatch(setSuccessMessage(true));
		} catch (error) {
			console.error('Failed to save changes:', error);
			dispatch(
				setError({
					exist: true,
					text: 'page.payment_categories.messages.saveFailed',
				})
			);
		} finally {
			setIsBatching(false);
		}
	}, [hasPendingChanges, pendingChanges, dispatch]);

	// Open sync confirmation dialog
	const handleSyncClick = useCallback(() => {
		setSyncDialogOpen(true);
	}, []);

	// Close sync dialog
	const handleSyncDialogClose = useCallback(() => {
		setSyncDialogOpen(false);
	}, []);

	// Sync all active categories to all companies
	const handleSyncConfirm = useCallback(async () => {
		setSyncDialogOpen(false);
		setIsSyncing(true);
		try {
			const result = await dispatch(PaymentCategoriesService.syncPaymentCategoriesToCompanies() as any);
			if (result) {
				dispatch(setSuccessMessage(true));
				// Update synced category IDs and reset the flag
				syncedCategoryIdsRef.current = getAllCategoryIds(originalCategories);
				setHasNewCategoriesSinceLastSync(false);
			}
		} catch (error) {
			dispatch(
				setError({
					exist: true,
					text: 'page.payment_categories.messages.syncFailed',
				})
			);
		} finally {
			setIsSyncing(false);
		}
	}, [dispatch, getAllCategoryIds, originalCategories]);

	return (
		<Fragment>
			<Page title={t('page.payment_categories.title')}>
				<WarningDialog
					open={errorDialogOpen}
					subTitle={t(errorDialogMessage)}
					handleCancel={handleErrorDialogClose}
				/>
				{/* Sync Confirmation Dialog */}
				<DialogBox
					openDialog={syncDialogOpen}
					handleClose={handleSyncDialogClose}
					handleApprove={handleSyncConfirm}
					handleReject={handleSyncDialogClose}
					title={t('page.payment_categories.sync.confirmTitle')}
					approveLabel={t('button.ok')}
					cancelLabel={t('button.cancel')}
				>
					<Stack spacing={2}>
						<Typography>{t('page.payment_categories.sync.confirmSubtitle')}</Typography>
						<Typography variant="body2" color="warning.main">
							{t('page.payment_categories.sync.warning')}
						</Typography>
					</Stack>
				</DialogBox>
				<Stack gap={4}>
					<Typography variant="body1" color={theme.palette.root.grey}>
						{t('page.payment_categories.description')}
					</Typography>

					<Box sx={{ maxWidth: '900px' }}>
						<Stack direction="row" justifyContent="flex-end" marginBottom={1}>
							<Button
								variant="outlined"
								startIcon={isSyncing ? <CircularProgress size={16} /> : <SyncIcon />}
								onClick={handleSyncClick}
								disabled={isSyncing || isLoading || !hasNewCategoriesSinceLastSync}
							>
								{t('page.payment_categories.button.syncToCompanies')}
							</Button>
						</Stack>
						<PaymentCategoryTree
							categories={workingCategories}
							isLoading={isLoading}
							onToggleActive={handleToggleActive}
							hasPendingChanges={hasPendingChanges}
							pendingChangesCount={pendingChangesCount}
							pendingChanges={pendingChanges}
							onSave={handleSave}
							onCancelChanges={handleCancelChanges}
							getPendingStatus={getPendingStatus}
						/>
					</Box>

					<Box
						sx={{
							maxWidth: '900px',
							marginTop: 4,
							padding: 2,
							backgroundColor: theme.palette.root.grayed,
							borderRadius: '8px',
						}}
					>
						<Typography variant="boldBody1" sx={{ marginBottom: 2 }}>
							{t('page.payment_categories.instructions.title')}
						</Typography>
						<Stack spacing={1}>
							<Typography variant="body2">
								• {t('page.payment_categories.instructions.clickToExpand')}
							</Typography>
							<Typography variant="body2">
								• {t('page.payment_categories.instructions.hoverForActions')}
							</Typography>
							<Typography variant="body2">
								• {t('page.payment_categories.instructions.depthLimit')}
							</Typography>
							<Typography variant="body2">
								• {t('page.payment_categories.instructions.checkConsole')}
							</Typography>
						</Stack>
					</Box>
				</Stack>
			</Page>
		</Fragment>
	);
};
